// PATH: src/components/modules/devtools/components/RichDiffViewer.js

import React, { useMemo, useState } from "react";
import {
  Box,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";

const classifyDiffLine = (line) => {
  if (line.startsWith("diff --git ") || line.startsWith("Index: ")) return "file";
  if (line.startsWith("@@")) return "hunk";
  if (line.startsWith("+++") || line.startsWith("---")) return "fileMeta";
  if (line.startsWith("+")) return "added";
  if (line.startsWith("-")) return "removed";
  return "context";
};

const parseUnifiedDiff = (diffText) =>
  String(diffText || "")
    .replace(/\r\n/g, "\n")
    .split("\n")
    .map((content, index) => ({
      id: `${index}-${content.slice(0, 16)}`,
      lineNumber: index + 1,
      content,
      type: classifyDiffLine(content),
    }));

const getRowSx = (type) => {
  if (type === "file") {
    return {
      backgroundColor: "grey.900",
      color: "common.white",
      fontWeight: 700,
    };
  }
  if (type === "hunk") {
    return {
      backgroundColor: "rgba(2, 136, 209, 0.08)",
      color: "info.dark",
      fontWeight: 700,
    };
  }
  if (type === "fileMeta") {
    return {
      backgroundColor: "grey.100",
      color: "text.secondary",
      fontWeight: 600,
    };
  }
  if (type === "added") {
    return {
      backgroundColor: "rgba(46, 125, 50, 0.08)",
      color: "success.dark",
    };
  }
  if (type === "removed") {
    return {
      backgroundColor: "rgba(211, 47, 47, 0.08)",
      color: "error.dark",
    };
  }
  return {
    backgroundColor: "background.paper",
    color: "text.primary",
  };
};

const getMarker = (type, content) => {
  if (type === "added") return "+";
  if (type === "removed") return "-";
  if (type === "hunk") return "@";
  if (type === "file" || type === "fileMeta") return "";
  return content ? " " : "";
};

const RichDiffViewer = ({
  diffText,
  emptyMessage = "No diff text available.",
  maxHeight = 520,
}) => {
  const [tab, setTab] = useState("rich");
  const hasDiff = Boolean(String(diffText || "").trim());
  const rows = useMemo(() => parseUnifiedDiff(diffText), [diffText]);

  if (!hasDiff) {
    return (
      <Typography variant="body2" color="text.secondary">
        {emptyMessage}
      </Typography>
    );
  }

  return (
    <Box>
      <Tabs
        value={tab}
        onChange={(event, nextValue) => setTab(nextValue)}
        sx={{ minHeight: 36, mb: 1 }}
      >
        <Tab value="rich" label="Rich diff" sx={{ minHeight: 36, py: 0.5 }} />
        <Tab value="raw" label="Raw diff" sx={{ minHeight: 36, py: 0.5 }} />
      </Tabs>

      {tab === "rich" ? (
        <Box
          sx={{
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 1,
            maxHeight,
            overflow: "auto",
            fontFamily: "monospace",
            fontSize: 12,
          }}
        >
          {rows.map((row) => (
            <Box
              key={row.id}
              sx={{
                ...getRowSx(row.type),
                display: "grid",
                gridTemplateColumns: "52px 24px minmax(0, 1fr)",
                minHeight: 22,
                borderBottom: "1px solid",
                borderBottomColor: "divider",
              }}
            >
              <Box
                sx={{
                  px: 1,
                  py: 0.25,
                  textAlign: "right",
                  color: row.type === "file" ? "grey.300" : "text.disabled",
                  userSelect: "none",
                  borderRight: "1px solid",
                  borderRightColor: row.type === "file" ? "grey.700" : "divider",
                }}
              >
                {row.lineNumber}
              </Box>
              <Box sx={{ px: 0.75, py: 0.25, userSelect: "none" }}>
                {getMarker(row.type, row.content)}
              </Box>
              <Box
                component="pre"
                sx={{
                  m: 0,
                  py: 0.25,
                  pr: 1,
                  overflowWrap: "anywhere",
                  whiteSpace: "pre-wrap",
                  font: "inherit",
                }}
              >
                {row.content || " "}
              </Box>
            </Box>
          ))}
        </Box>
      ) : (
        <Box
          component="pre"
          sx={{
            m: 0,
            p: 1.25,
            backgroundColor: "grey.100",
            borderRadius: 1,
            maxHeight,
            overflow: "auto",
            fontFamily: "monospace",
            fontSize: 12,
            whiteSpace: "pre-wrap",
          }}
        >
          {diffText}
        </Box>
      )}
    </Box>
  );
};

export default RichDiffViewer;
